import React, { useMemo, useState } from 'react';
import { PlusCircle } from 'react-bootstrap-icons';

import MedicationProfileTitle from './components/MedicationProfileTitle';
import MedicationProfileRow from './components/MedicationProfileRow';
import { useSelector } from 'react-redux';
import { SystemPermission } from '../../../context';
import { RootState } from '../../../store';
import { MedProfile, MedProfileStatus } from './hook/types';
import { Row } from 'react-bootstrap';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import CaremapprConfirmation from '../../components/Confirmation/CaremapprConfirmation';
import { useToast } from '../../../context';

const MedicationProfileRecords = ({
  onChangeTab,
  medProfiles,
  isDeleting,
  onDeleteMedProfile,
  onDuplicateMedProfile,
  onCreateBlankMedProfile,
  isLoadingMedProfiles,
}: {
  onChangeTab: any;
  medProfiles: MedProfile[];
  onDeleteMedProfile: (id: string) => Promise<void>;
  onDuplicateMedProfile: (id: string) => void;
  onCreateBlankMedProfile: () => void;
  isDeleting: string;
  profileStatus?: MedProfileStatus;
  isLoadingMedProfiles: boolean;
}) => {
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile, // When TSifying this file
  );
  const { setToast } = useToast();

  const canEditMedicationProfile =
    !selectedTenancy?.permissionsSetup ||
    (selectedTenancy?.permissionsSetup &&
      selectedTenancy?.userPermissions &&
      !!(selectedTenancy?.userPermissions)[
        SystemPermission.EDIT_MEDICATION_PROFILE
      ]);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const currentMedProfiles = useMemo(
    () =>
      medProfiles
        .filter((data) => data.isCurrent)
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        ),
    [medProfiles],
  );
  const pastMedProfiles = useMemo(
    () =>
      medProfiles
        .filter((data) => !data.isCurrent)
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        ),
    [medProfiles],
  );

  return (
    <div className="medication-profile-body" style={{ position: 'relative' }}>
      {/* @ts-expect-error */}
      <MedicationProfileTitle
        actions={
          canEditMedicationProfile
            ? [
                {
                  name: 'Create new medication profile',
                  icon: <PlusCircle />,
                  onClickAction: onCreateBlankMedProfile,
                },
              ]
            : []
        }
        title="Medication profile"
        titleClassName="medication-profile-title"
      />

      {currentMedProfiles.length === 0 && !isLoadingMedProfiles && (
        <Row className="align-items-start px-4">
          No profiles from current admission
        </Row>
      )}
      {currentMedProfiles.map((data) => (
        //@ts-expect-error
        <MedicationProfileRow
          key={data.id}
          data={data}
          onChangeTab={onChangeTab}
          onDelete={onDeleteMedProfile}
          canEdit={!!canEditMedicationProfile}
          isDeleting={isDeleting}
          onDuplicate={onDuplicateMedProfile}
          onOpenDeleteConfirm={(id: string) => setConfirmDeleteId(id)}
        />
      ))}
      <MedicationProfileTitle
        title="Older profiles"
        titleClassName="medication-profile-font-gray"
        //@ts-expect-error
        onClickAction={() => console.log('olders')}
      />
      {pastMedProfiles.length === 0 && !isLoadingMedProfiles && (
        <Row className="align-items-start px-4">
          No profiles from previous admissions
        </Row>
      )}
      {pastMedProfiles.map((data) => (
        //@ts-expect-error
        <MedicationProfileRow
          key={data.id}
          data={data}
          onChangeTab={onChangeTab}
          onDelete={onDeleteMedProfile}
          canEdit={!!canEditMedicationProfile}
          isDeleting={isDeleting}
          onDuplicate={onDuplicateMedProfile}
          onOpenDeleteConfirm={(id: string) => setConfirmDeleteId(id)}
        />
      ))}
      {isLoadingMedProfiles && <LoadingSpinner size={16} />}
      <CaremapprConfirmation
        open={!!confirmDeleteId}
        text="Mark as entered in error?"
        icon="warning"
        confirmButtonText="Mark as entered in error"
        cancelButtonText="Cancel"
        onConfirm={async () => {
          if (!confirmDeleteId) return;
          try {
            await onDeleteMedProfile(confirmDeleteId);
          } catch (error) {
            setToast('Entering profile in error failed.', { autohide: true });
          }
        }}
        onCancel={() => setConfirmDeleteId(null)}
        onClose={() => setConfirmDeleteId(null)}
      />
    </div>
  );
};

export default MedicationProfileRecords;
